import React, { memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import NavBar from 'components/NavBar';
import Cookies from 'universal-cookie';
import NavMenu from 'components/NavMenu';
import FullMenu from '../../views/system/FullMenu';
import Dashboard from 'views/dashboard/DashboardForm';
import SystemCompanyWorkspace from 'views/system/SystemCompanyWorkspace';
import RouteModal from 'components/RouteModal';
import { appActions } from 'state/modules/app';
import ComponentMutation from 'utils/ComponentMutation';
import { Route, Switch } from 'react-router-dom';
import * as Breakpoints from 'utils/ResponsiveBreakpoints';
import { serviceActions } from 'state/modules/service';
import CurrentCompanyQuery from '../../graphql/queries/company/CurrentCompanyQuery.graphql';
import SystemUserTabOrders from 'graphql/queries/system/SystemUserTabOrderQuery.graphql';
import SystemCompanySwitchMutation from 'graphql/mutations/system/SystemCompanySwitchMutation.graphql';
import { createMenuForStore, filterMenuOnPermissionsModalsSystem } from 'utils/MenuPermissions';
import LoginModal from './LoginModal';
import { client } from 'client';
import styled from 'styled-components';
import CustomerSidebarForm from 'views/customer/CustomerSidebarForm';
import SupplierSidebarForm from 'views/supplier/SupplierSidebarForm';
import ProductSidebarForm from 'views/product/ProductSidebarForm';
import AppContext from '../../components/Context/AppContext';
import UserContext from '../../components/Context/UserContext';
import { useHistory, useLocation } from 'react-router';
import getClientMutationId from '../../utils/getClientMutationId';
import { AlertType, CompanyUserTheme } from '../../graphql/schema/p4types';
import { modalRoutes, systemRoutes } from '../../components/MenuOptions/useMenuOptions';
import SuspenseFallback from './SuspenseFallback';
import { FluentProvider, makeStyles, mergeClasses } from '@fluentui/react-components';
import { useId } from '@fluentui/react-hooks';
import { NotificationsArea } from './NotificationsArea';
import MailItemSidebar from './MailItemSidebar';
import {
  p4DarkHighContrastTheme,
  p4DarkTheme,
  p4LightHighContrastTheme,
  p4LightTheme,
  p4Tokens,
} from '../../styles/themes/default/p4FluentTheme';
import { ApplyToBody } from './ApplyToBody';
import * as Sentry from '@sentry/react';
import SentryFallback from '../../components/Sentry/SentryFallback';
import SentryRoute from '../../components/Sentry/SentryRoute';
import { SentryProvider } from '../../components/Sentry/SentryProvider';
import { SideBarCloseIcon } from '../../components/SideBar/SideBarCloseIcon';
import UserChangeMonitor from './UserChangeMonitor';
import { ChunkErrorHandler } from '../../components/modals/ChunkErrorHandler';
import DocumentProvider from '../../components/document/DocumentProvider';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useCurrentUser } from './useCurrentUser';

const APP_MENU_HIDDEN = 'appMenuHidden';

const Sidebar = styled.div`
  order: 1;
  z-index: 1000000;
  background-color: #fff;
  flex: 0 0 300px;
  height: 100%;
  position: relative;
`;

const createRoutes = (menu) => {
  const options = Object.keys(menu);
  const routes = [];
  for (let i = 0; i < options.length; i++) {
    const option = menu[options[i]];
    const { url, component, exact } = option;
    if (url === '/Sales/TradeCounter/Details/New') continue;
    if (url && component) {
      routes.push(<SentryRoute key={url} path={url} component={component} exact={exact} />);
    } else if (!url) routes.push(...createRoutes(option));
  }
  return routes;
};

const createModalRoutes = (fullUserDetails, app) => {
  const options = filterMenuOnPermissionsModalsSystem(
    modalRoutes,
    fullUserDetails.companyRoleActions,
    fullUserDetails.systemUser.admin
  );
  const newRoutes = [];
  for (let i = 0; i < options.length; i++) {
    const option = options[i];
    const { url, component, render, exact } = option;
    if (url && component) {
      newRoutes.push(
        <Route exact={exact} key={url} path={url}>
          <RouteModal component={component} app={app} />
        </Route>
      );
    } else if (url && render) {
      newRoutes.push(<SentryRoute exact={exact} key={url} path={url} render={render} />);
    }
  }
  return newRoutes;
};

const createSystemRoutes = (fullUserDetails) => {
  const options = filterMenuOnPermissionsModalsSystem(
    systemRoutes,
    fullUserDetails.companyRoleActions,
    fullUserDetails.systemUser.admin
  );
  const newRoutes = [];
  for (let i = 0; i < options.length; i++) {
    const option = options[i];
    const { url, component, render } = option;
    if (url && component) {
      newRoutes.push(<SentryRoute key={url} path={url} component={component} />);
    } else if (url && render) {
      newRoutes.push(<SentryRoute key={url} path={url} render={render} />);
    }
  }
  return newRoutes;
};

/**
 * Standard method for initiating a logout from P4 with appropriate cleanup.
 */
const useLogout = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  return useCallback(() => {
    history.replace('/Login');
    //Resets store, then sets authorization state in redux appropriately.
    dispatch(appActions.logoutUser());
  }, [dispatch]);
};

const useAppStyles = makeStyles({
  main: {
    paddingTop: 0,
    position: 'fixed',
    top: '60px', //When not in fullScreen, we need to account for the height of the NavBar, which is fixed at 60px.
  },
  mainFullScreen: {
    top: 0, //When in fullScreen, the NavBar is not displayed, so we can fill the available height.
  },
  appContent: {
    position: 'relative',
    height: 'calc(100% - 60px)', //When not in fullScreen, we need to account for the height of the NavBar, which is fixed at 60px.
    width: '100%',
    order: 2,
    outline: 'none',
    zIndex: 1,
    backgroundColor: p4Tokens.colorNeutralBackground1,
    minWidth: 0,
  },
  appContentFullScreen: {
    height: '100%', //When in fullScreen, the NavBar is not displayed, so we can fill the available height.
  },
});

const App = (props) => {
  const routerLocation = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  //app selectors.
  const redirectToLogin = useSelector((store) => store.app.redirectToLogin);
  const isLoggedIn = useSelector((store) => store.app.isLoggedIn);
  const reload = useSelector((store) => store.app.reload);
  const returnURL = useSelector((store) => store.app.returnURL);
  const unAuthorized = useSelector((store) => store.app.unAuthorized);
  const appUser = useSelector((store) => store.app.user);
  const appCompany = useSelector((store) => store.app.company);
  const fullScreen = useSelector((store) => store.app.fullScreen);
  const fullUserDetails = useSelector((store) => store.app.fullUserDetails);
  const appMenuHidden = useSelector((store) => store.app.appMenuHidden);
  const appSidebarType = useSelector((store) => store.app.appSidebarType);
  const appSidebarHidden = useSelector((store) => store.app.appSidebarHidden);
  const appSidebarId = useSelector((store) => store.app.appSidebarId);
  const mailItemId = useSelector((store) => store.app.mailItemId);
  const mailItemTo = useSelector((store) => store.app.mailItemTo);
  const focusArea = useSelector((store) => store.app.focusArea);
  const autoFocus = useSelector((store) => store.app.autoFocus);
  const featureFlags = useSelector((store) => store.app.fullUserDetails?.flags);

  const menu = useSelector((store) => store.menu);

  const appStyles = useAppStyles();

  if (redirectToLogin === true) {
    history.replace('/Login');
  }

  if (reload === true) {
    window.location.reload(true);
  }

  const [enterLogin, setEnterLogin] = useState(false);

  //Refs for Immutable Values:
  const routes = useRef([]);
  const modalRoutes = useRef([]);
  const systemRoutes = useRef([]);
  const previousNonModalLocation = useRef(routerLocation);
  const cookies = useRef(new Cookies());

  //Refs for components
  const navBarRef = useRef();
  const navMenuRef = useRef();
  const appContentRef = useRef();

  const toasterId = useId('toaster');
  const logout = useLogout();

  // Note - It's very important that this function doesn't change after initial render.
  // 'dispatch' should be a safe dependency, but care needed if we add others.
  const resetRoutes = useCallback(
    (currentUser) => {
      const storeMenu = createMenuForStore(currentUser.companyRoleActions, currentUser.systemUser.admin);
      dispatch(appActions.addMenuToStore(storeMenu));

      routes.current = createRoutes(storeMenu);
      //Merging user/menu into the passed 'app' since these won't have updated into the store yet.
      //Though not sure if they're actually required, can possible cut this. (Really, the components should fetch from redux directly).
      modalRoutes.current = createModalRoutes(currentUser, { user: currentUser.systemUser, menu: storeMenu });
      routes.current.push(modalRoutes.current);
      systemRoutes.current = createSystemRoutes(currentUser);
    },
    [dispatch]
  );

  const { data: userData, refetch: refetchUser, loading: userLoading } = useCurrentUser({ logout, resetRoutes });

  const currentUser = userData?.currentUser;
  const { data: company } = useQuery(CurrentCompanyQuery, {
    skip: !userData?.currentUser?.companyUser,
  });

  const { data: systemUserTabOrders, refetch: refetchTabOrders } = useQuery(SystemUserTabOrders, {
    variables: { systemUserId: currentUser?.systemUser?.id },
    skip: !currentUser,
  });

  //Clear 'fullscreen' mode when existing fullscreen.
  //(Put here to prevent accidentally being 'stuck' without menus after using trade counter).
  const disableFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      dispatch(appActions.toggleFullScreen(false));
    }
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', disableFullScreen);

    return () => {
      document.removeEventListener('fullscreenchange', disableFullScreen);
      if (isLoggedIn && returnURL) {
        history.push({
          pathname: returnURL,
          state: { previousLocation: history.location?.pathname || 'back' },
        });
      }
      autoHideMenu();
    };
  }, []);

  //Note: Possible a bit overkill on the deps but would rather not risk breaking anything.
  useEffect(() => {
    if (!userLoading && !isLoggedIn && routerLocation.pathname !== '/Authenticate') {
      if (unAuthorized && appUser) {
        if (!enterLogin) {
          dispatch(appActions.storeURL(routerLocation.pathname));
          setEnterLogin(true);
        }
      }
    } else if (enterLogin) {
      dispatch(appActions.storeURL(routerLocation.pathname));
      setEnterLogin(false);
    }
  }, [enterLogin, userLoading, unAuthorized, appUser, isLoggedIn, routerLocation, routerLocation.pathname]);

  useEffect(() => {
    if (routerLocation.state?.modal) {
      //Never set a modal route as the previous location - Will result in undesirable behaviours like not refreshing data that has changed.
      return;
    }

    //Original Check - If you navigate 'positively' (E.g. open a screen via a link) and the new location is not a modal, update the previous location for later back navigations.
    const updatePreviousLocationOnPositiveNavigation =
      history.action !== 'POP' && (!routerLocation.state || !routerLocation.state?.modal);

    //New Check - If you navigate 'negatively' (E.g. 'Back/Cancel') and the previousLocation doesn't match - Set it to the current location.
    //This resolves a problem when you navigate through multiple levels that causes a nested screen to be considered as the 'modal' to go back to.
    //I.e. Workspace should be kept open, but because you went workspace -> PO -> Assign to Sales -> Back to PO -> Back to Workspace, the 'previousLocation' is now 'Assign to Sales' - Not correct!
    const updatePreviousLocationOnNegativeNavigation =
      history.action === 'POP' && routerLocation.pathname !== previousNonModalLocation.current?.pathname;

    if (updatePreviousLocationOnPositiveNavigation || updatePreviousLocationOnNegativeNavigation) {
      previousNonModalLocation.current = routerLocation;
    }
  }, [history.action, routerLocation, routerLocation.state?.modal]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.has('mailItemId') && appUser?.msftAccessTokenSet) {
      dispatch(appActions.toggleMailSidebar(false, queryParams.get('mailItemId'), queryParams.get('mailItemTo')));
    }
  }, [window.location.search, appUser]);

  const theme = useMemo(() => {
    switch (currentUser?.companyUser?.companyUserTheme) {
      default:
        return p4LightTheme;
      case CompanyUserTheme.LightHighContrast:
        return p4LightHighContrastTheme;
      case CompanyUserTheme.Dark:
        return p4DarkTheme;
      case CompanyUserTheme.DarkHighContrast:
        return p4DarkHighContrastTheme;
    }
  }, [currentUser?.companyUser?.companyUserTheme]);

  const autoHideMenu = () => {
    if (
      (!props.appMenuHidden && window.innerWidth <= Breakpoints.TABLET_BREAKPOINT) ||
      cookies.current.get(APP_MENU_HIDDEN) === 'true'
    ) {
      dispatch(appActions.toggleMenu(true));
    }
  };

  const switchCompany = (id) => {
    if (appCompany.id === id) return;

    //TODO - Replace Mutation with Hook.
    ComponentMutation(
      SystemCompanySwitchMutation,
      {
        clientMutationId: getClientMutationId(),
        systemCompany: {
          id: id,
        },
      },
      true
    )
      .then(() => refetchUser(true))
      .then(() => {
        dispatch(serviceActions.switchCompany());
        return client.resetStore();
      })
      .then(() => {
        props.history.push({ pathname: '/' });
        dispatch(serviceActions.genericAlert('Switch Company', 'Company switch completed', AlertType.Success));
      });
  };

  const handleKeyDown = (event) => {
    if (!event.altKey) {
      return;
    }
    let area = '';
    switch (event.keyCode) {
      case 65: // 'A' -> Focus on Application Bar area
        area = 'App';
        break;
      case 77: // 'M' -> Focus on Menu area
        area = 'Menu';
        break;
      case 87: // 'W' -> Focus on main Work area
        area = 'Work';
        break;
    }
    if (area !== '') {
      event.preventDefault();
      doFocusArea(area);
    }
  };

  const doFocusArea = (area, forceFocus) => {
    let container;
    switch (area) {
      case 'App':
        container = navBarRef.current;
        break;
      case 'Menu':
        container = navMenuRef.current;
        break;
      case 'Work':
        container = appContentRef.current;
        break;
    }

    // Auto focus first element if focus is currently outside the area container or if
    // focus is on the container for the given area and we are switching areas
    let _autoFocus = (container instanceof HTMLElement && !container.contains(document.activeElement)) || forceFocus;
    const switchArea = focusArea !== area;
    if (container === document.activeElement) {
      _autoFocus = switchArea;
    }

    // Update area with current focus if previous area was not the requested area
    // If autoFocus was requested in last update then need to call focus area again with autoFocus false to
    //   clear the autoFocus attribute
    // If autoFocus is required then need to call focus area again with autoFocus true - this can happen even
    //   if the area is not changing due to clicking on the app container area
    if (switchArea || autoFocus || _autoFocus) dispatch(appActions.focusArea(area, _autoFocus));
  };

  const handleOnMenuClick = (menuState) => {
    cookies.current.set(APP_MENU_HIDDEN, menuState, { path: '/' });
    dispatch(appActions.toggleMenu(menuState));
  };

  if (!currentUser) return null;
  //Added requirement that last action was 'Push' so we don't trigger modal behaviour 'backwards' (E.g. Workspace should stay open when PO is opened, but assignment child should not stay open when you go 'back' from it).
  const isModal =
    !!(
      routerLocation.state &&
      routerLocation.state.modal &&
      previousNonModalLocation.current &&
      previousNonModalLocation.current !== routerLocation
    ) &&
    (history.action === 'PUSH' || history.action === 'REPLACE'); //Added replace - Allow 'Save & Continue' to still maintain previous location route.

  const mainClassname =
    (routerLocation.state && routerLocation.state.login) || fullScreen
      ? mergeClasses(appStyles.main, appStyles.mainFullScreen)
      : appStyles.main;
  const appContentClassname = fullScreen
    ? mergeClasses(appStyles.appContent, appStyles.appContentFullScreen)
    : appStyles.appContent;
  const navBar =
    (routerLocation.state && routerLocation.state.login) || fullScreen ? null : (
      <NavBar
        ref={navBarRef}
        menuState={!appMenuHidden}
        onMenuClick={handleOnMenuClick}
        onLogoutClick={logout}
        user={currentUser.systemUser}
        company={currentUser.currentCompany}
        tabIndex={focusArea === 'App' ? 1 : -1}
        focusArea={doFocusArea}
        autoFocus={focusArea === 'App' && autoFocus}
        onCompanyClick={switchCompany}
        history={history}
        dispatch={dispatch}
        featureFlags={featureFlags}
      />
    );

  const fullMenu = (props) => {
    return <FullMenu menu={menu} {...props} />;
  };

  let SidebarForm;
  switch (appSidebarType) {
    case 'ProductForm':
      SidebarForm = ProductSidebarForm;
      break;
    case 'SupplierForm':
      SidebarForm = SupplierSidebarForm;
      break;
    default:
      SidebarForm = CustomerSidebarForm;
      break;
  }

  return (
    <SentryProvider userDetails={fullUserDetails}>
      <AppContext.Provider
        value={{
          location: routerLocation,
          history: history,
          user: appUser,
          fullUserDetails: fullUserDetails,
          company: company?.company,
          refetchUser: refetchUser,
          toasterId,
        }}
      >
        <UserContext.Provider value={{ currentUser, systemUserTabOrders, refetchTabOrders }}>
          <DocumentProvider>
            <Sentry.ErrorBoundary fallback={SentryFallback}>
              <ChunkErrorHandler />
              <FluentProvider theme={theme}>
                <ApplyToBody />
                <div
                  id="app-container"
                  className={!appMenuHidden ? 'app-container toggled' : 'app-container'}
                  onKeyDown={handleKeyDown}
                >
                  <UserChangeMonitor refetchUser={refetchUser} />
                  {enterLogin ? (
                    <LoginModal
                      userName={fullUserDetails.systemUser.username}
                      currentCompany={fullUserDetails.currentCompany}
                      dispatch={dispatch}
                    />
                  ) : null}
                  <header>{navBar}</header>
                  <main className={mainClassname}>
                    {!fullScreen ? (
                      <>
                        <NavMenu
                          ref={navMenuRef}
                          menuState={!appMenuHidden}
                          onMenuClick={handleOnMenuClick}
                          currentUser={currentUser}
                          company={currentUser.currentCompany}
                          location={routerLocation}
                          onLogoutClick={logout}
                          onCompanyClick={switchCompany}
                          tabIndex={focusArea === 'Menu' ? 1 : '-1'}
                          focusArea={doFocusArea}
                          autoFocus={focusArea === 'Menu' && autoFocus}
                          customMenu={menu}
                          history={history}
                          clearPreviousLocation={() => {
                            //When menu navigations occur, we should clear the previous location.
                            //This is so we don't erroneously load irrelevant modal routes from the previous location.
                            previousNonModalLocation.current = null;
                          }}
                        />
                        {!appSidebarHidden ? (
                          <Sidebar>
                            {appSidebarType === 'MailItem' ? (
                              <MailItemSidebar
                                itemId={mailItemId}
                                user={mailItemTo}
                                onClose={() => dispatch(appActions.toggleMailSidebar(true))}
                              />
                            ) : (
                              <>
                                <SideBarCloseIcon left={appMenuHidden ? '65px' : '255px'} />
                                <SidebarForm id={appSidebarId} sidebar={true} />
                              </>
                            )}
                          </Sidebar>
                        ) : null}
                      </>
                    ) : null}
                    <div id={'app-content'} ref={appContentRef} className={appContentClassname} tabIndex="-1">
                      <div style={{ height: '100%' }}>
                        <Suspense fallback={<SuspenseFallback />}>
                          <Switch location={isModal ? previousNonModalLocation.current : routerLocation}>
                            <SentryRoute path="/" exact component={company ? Dashboard : SystemCompanyWorkspace} />
                            <SentryRoute path="/Dashboard" exact component={Dashboard} />
                            <SentryRoute path="/Dashboard/:id" exact component={Dashboard} />
                            <SentryRoute path="/menu/:module/:submodule" exact component={fullMenu} />
                            <SentryRoute path="/menu/:module" component={fullMenu} />
                            {routes.current}
                            {systemRoutes.current}
                            {modalRoutes.current}
                          </Switch>
                          {isModal ? modalRoutes.current : null}
                        </Suspense>
                      </div>
                    </div>
                    <NotificationsArea currentUser={currentUser} toasterId={toasterId} />
                  </main>
                </div>
              </FluentProvider>
            </Sentry.ErrorBoundary>
          </DocumentProvider>
        </UserContext.Provider>
      </AppContext.Provider>
    </SentryProvider>
  );
};

const MemoizedApp = memo(App, (prev, next) => {
  return prev.location === next.location;
});

export default (props) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <MemoizedApp {...props} />
    </DndProvider>
  );
};
